import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@mui/material';
import { ItemsListFC } from '@provider-portal/menu-common/components/ItemsList/ItemsListFC';
import { PromotionsAnalyticsEvents } from '@provider-portal/promotions-common/analyticsEvents';
import _ from 'lodash';
import { createPromotionsApiFetchers } from 'promotions';
import { ListItemElement, useMenuItemsLoader } from 'promotions/hooks/useMenuItemsLoader';

import { useConstant } from 'hooks/useConstant';
import { $tk } from 'utils/appTranslationKey';

import { useAppServices } from 'components/AppServicesProvider';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import { SelectedMenuItem } from 'root/promotions/types';

interface MenuItemSelectorProps {
  providerId: number;
  setMenuItemsSelected: React.Dispatch<React.SetStateAction<SelectedMenuItem[]>>;
  preselectedProductIds: string[];
  exclusionTags: string[];
  exclusionMessage: string;
}
const MAX_SELECTED_ITEMS_CHIPS = 10;
export function MenuItemSelector({
  providerId,
  setMenuItemsSelected,
  preselectedProductIds,
  exclusionTags,
  exclusionMessage,
}: MenuItemSelectorProps) {
  const { apiClient } = useAppServices();
  const { mixpanelService } = useCommonAppServices();
  const { fetchers } = useConstant(() => ({
    fetchers: createPromotionsApiFetchers(apiClient),
  }));

  const menuItemsList: ListItemElement[] = useMenuItemsLoader(
    providerId,
    fetchers.getMenuItemsForProviderActiveMenu.useFetcher(),
    exclusionTags,
    exclusionMessage,
  );

  const intl = useIntl();
  const strings = useMemo(
    () => ({
      components: {
        addItems: {
          listComponent: {
            addLabel: intl.formatMessage({ id: $tk.components.ModifierAddEdit.addItems }),
          },
          viewMore: (num: number) =>
            intl.formatMessage({ id: $tk.components.ModifierAddEdit.viewMore }, { count: num }),
          dialogComponent: {
            cardTitle: intl.formatMessage({ id: $tk.components.ModifierAddEdit.cardTitle }),
            searchLabel: intl.formatMessage({ id: $tk.components.ModifierAddEdit.searchLabel }),
            noSearchResultsTitle: intl.formatMessage({
              id: $tk.components.ModifierAddEdit.noSearchResultsTitle,
            }),
            noSearchResultsSubtitle: intl.formatMessage({
              id: $tk.components.ModifierAddEdit.noSearchResultsSubtitle,
            }),
            cancel: intl.formatMessage({ id: $tk.buttons.cancel }),
            addItems: intl.formatMessage({ id: $tk.buttons.addItems }),
          },
        },
      },
    }),
    [intl],
  );

  const [selectedStableContextIds, setSelectedStableContextIds] = useState<string[]>([]);

  const menuItemMap = useMemo(() => {
    return menuItemsList.reduce((acc, item) => {
      acc[item.id] = {
        globalStableContextId: item.id,
        menuItemName: item.name,
        price: item.priceDetails.price,
        currency: item.priceDetails.currency,
        productId: item.product_id,
      };
      // Creating map for both stableContextId and product_id for backward compatibility
      acc[item.product_id] = {
        globalStableContextId: item.id,
        menuItemName: item.name,
        price: item.priceDetails.price,
        currency: item.priceDetails.currency,
        productId: item.product_id,
      };
      return acc;
    }, {} as Record<string, SelectedMenuItem>);
  }, [menuItemsList]);

  const setSelectedMenuItems = useCallback(
    (selectedProductIds) => {
      setMenuItemsSelected(
        _.compact(selectedProductIds?.map((productId) => menuItemMap[productId])),
      );
      const stableContextIds = selectedProductIds?.map(
        (productId) => menuItemMap[productId]?.globalStableContextId,
      );
      setSelectedStableContextIds(stableContextIds ?? []);
    },
    [menuItemMap, setMenuItemsSelected],
  );

  useEffect(() => {
    setSelectedMenuItems(
      preselectedProductIds.filter((productId) => Boolean(menuItemMap[productId])),
    );
  }, [preselectedProductIds, setSelectedMenuItems, menuItemMap]);

  const onItemsSelectHandler = useCallback(
    (stableContextIds: string[]) => {
      const productIds = stableContextIds.map(
        (stableContextId) => menuItemMap[stableContextId]?.productId,
      );
      setSelectedMenuItems(productIds);
    },
    [setSelectedMenuItems, menuItemMap],
  );
  const [isItemListOpen, setIsItemListOpen] = useState<boolean>(false);
  const onItemsToggle = useCallback(() => {
    if (!isItemListOpen) {
      setIsItemListOpen(true);
      mixpanelService.trackProviderEvent(PromotionsAnalyticsEvents.opened_menu_selector, {
        providerId,
      });
    }
  }, [isItemListOpen, mixpanelService, providerId]);

  return (
    <Box mb={2}>
      <ItemsListFC
        strings={strings.components.addItems}
        selectedItems={selectedStableContextIds}
        itemsList={menuItemsList}
        onSelectedValuesChange={onItemsSelectHandler}
        onItemListToggle={onItemsToggle}
        shouldShowImages
        maxSelectedItemsChips={MAX_SELECTED_ITEMS_CHIPS}
      />
    </Box>
  );
}
